/**
 * /admin/raw-messages — debug browser for raw_messages.
 *
 * Useful when ingest looks broken: filter by group + text, see what's
 * actually landing in DB. Cursor pagination on ts (newest first).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  type GroupSummary,
  type RawMessageBrowseResponse,
  Routes,
} from '@tg-leads/contracts';

import { api } from '../../lib/api';

export function RawMessagesPage() {
  const [groupId, setGroupId] = useState('');
  const [text, setText] = useState('');

  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () =>
      api.get<{ items: GroupSummary[]; nextCursor: string | null }>(
        Routes.admin.groups,
      ),
  });

  const msgs = useQuery({
    queryKey: ['rawMessages', groupId, text],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (groupId) qs.set('groupId', groupId);
      if (text.trim()) qs.set('text', text.trim());
      qs.set('limit', '100');
      return api.get<RawMessageBrowseResponse>(
        `${Routes.admin.rawMessages}?${qs}`,
      );
    },
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">原始消息浏览</h1>
        <p className="text-sm text-slate-500 mt-1">
          排查采集环节是否正常时用。可按群和文本子串筛选；最新消息在最上面。
        </p>
      </div>

      <div className="card">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="label">来源群</label>
            <select
              className="input"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">— 所有群 —</option>
              {(groups.data?.items ?? []).map((g) => (
                <option key={g.id} value={g.id}>
                  {g.title}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">文本中包含</label>
            <input
              className="input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="USDT / 求购 / 任何子串…"
            />
          </div>
        </div>
      </div>

      <div className="card overflow-x-auto">
        {msgs.isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : !msgs.data?.items.length ? (
          <div className="text-slate-500 text-sm">没有匹配的消息</div>
        ) : (
          <ul className="divide-y -mx-4">
            {msgs.data.items.map((m) => (
              <li key={m.id} className="px-4 py-2.5">
                <div className="flex gap-3 text-[10px] text-slate-500 mb-1">
                  <span>{new Date(m.ts).toLocaleString()}</span>
                  <span className="truncate max-w-[20rem]">{m.groupTitle}</span>
                  <span className="font-mono">
                    {m.senderUsername
                      ? `@${m.senderUsername}${m.senderFirstName ? ` · ${m.senderFirstName}` : ''}`
                      : m.senderFirstName
                        ? m.senderFirstName
                        : `id ${m.senderUserId}`}
                  </span>
                  <span className="font-mono text-slate-400">{m.tgChatId}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap break-words">
                  {m.text}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
